/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { SessionCreateDrmTypeEnum } from 'client-code/session-manager';
import {
    TransportEncodingType,
    VideoProviderConfiguration,
} from 'client-code/video-provider-configurations';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { Constants } from '../constants';
import { DeviceModel } from '../device';
import { ErrorTypes, LiveTvError } from '../error';
import { ServicesClientImpl } from '../interactors';
import { IServices } from '../interfaces/IServices';
import { Log, QoEAppLogger } from '../logging';
import { PlaybackDataModelImpl } from '../playback/impls';
import { Storage } from '../storage/Storage';
import { ITivoSubscription, TivoSubscriptionModel } from '../tivo';
import { CcbSwitch } from '../utils/ccb';
import { CcbSwitchName } from '../utils/ccb/enums';
import { PlayerViewMode } from './enums';
import { LiveTvPlaybackErrorMapper } from './error';
import { TrickplayControlImpl } from './impls/TrickplayControlImpl';
import {
    IPlayable,
    IPlayerStats,
    IPlayerStatus,
    IPlayerView,
    ISeekRange,
    ITextTrack,
    ITrickplayControl,
    IVariantTrack,
    IVideoManager,
    IVideoPlayer,
    IVpcDrmInfo,
} from './interfaces';
import { ShakaVideoPlayerImpl } from './shaka/ShakaVideoPlayerImpl';

// NOTE: This file is sorted category wise.
/* eslint-disable class-methods-use-this */
export class VideoManager implements IVideoManager, IPlayerView {
    private static TAG: string = 'VideoManager';

    private static CssClasses = {
        HOME_SCREEN_VIDEO_WINDOW: 'home-screen-video-window',
        HIDDEN: 'hidden',
        MAXIMIZE: 'maximize',
        MINIMIZE: 'minimize',
    };

    private mDrmServerType: string;

    private mLVPIntervalId: any | null;

    private mPartnerProviderId: string;

    private mPlayable: IPlayable | null;

    private mPlayerViewMode: PlayerViewMode;

    private mPlayerViewModeSubscription = new TivoSubscriptionModel<PlayerViewMode>();

    private mShakaPlayer: IVideoPlayer;

    private mStreamingUrl: string;

    private mTransportEncodingType: string;

    private mTrickplayControl: ITrickplayControl;

    private mUiContainerRef: HTMLDivElement | null;

    private mVideoEl: HTMLVideoElement | null;

    public constructor() {
        this.mDrmServerType = '';
        this.mPartnerProviderId = '';
        this.mPlayable = null;
        this.mPlayerViewMode = PlayerViewMode.VideoWindow;
        this.mShakaPlayer = new ShakaVideoPlayerImpl();
        this.mStreamingUrl = '';
        this.mTransportEncodingType = '';
        this.mTrickplayControl = new TrickplayControlImpl(this.mShakaPlayer);
        this.mUiContainerRef = null;
        this.mVideoEl = null;
    }

    // eslint-disable-next-line class-methods-use-this
    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](VideoManager.TAG, message, ...optionalParams);
        Log[level[0]](VideoManager.TAG, message, ...optionalParams);
        if (level === 'error')
            Diagnostics.error(VideoManager.TAG, 'stacktrace:,', optionalParams[0]);
    }

    private fetchVPConfigurations(forceNetworkCall: boolean): void {
        this.log('debug', 'fetchVPConfigurations');

        if (this.mPartnerProviderId && this.mPartnerProviderId.length) {
            const services: IServices = new ServicesClientImpl();
            services
                .fetchVPConfigurationsForVideoPlayer(this.mPartnerProviderId, forceNetworkCall)
                .then((response) => {
                    const isInvalidResponse = this.setUpDrm(response);
                    if (isInvalidResponse && !forceNetworkCall) {
                        this.fetchVPConfigurations(true);
                    } else if (isInvalidResponse && forceNetworkCall) {
                        if (response.length === 0) {
                            this.getPlaybackErrorListener().notify(
                                new LiveTvPlaybackErrorMapper().getErrorByCode(
                                    ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934,
                                ),
                            );
                        } else {
                            this.getPlaybackErrorListener().notify(
                                new LiveTvPlaybackErrorMapper().getErrorByCode(
                                    ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909,
                                ),
                            );
                        }
                        this.mShakaPlayer.resetPlayerConfiguration();
                    }
                })
                .catch((error) => {
                    this.getPlaybackErrorListener().notify(
                        new LiveTvPlaybackErrorMapper().getErrorByCode(
                            ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934,
                        ),
                    );
                    this.log('error', 'fetchVPConfigurations', JSON.stringify(error));
                });
        } else {
            this.getPlaybackErrorListener().notify(
                new LiveTvPlaybackErrorMapper().getErrorByCode(
                    ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909,
                ),
            );
        }
    }

    /**
     * This function formats the play uri from channel as per the player requirements.
     * @returns formatted playUri.
     */
    // eslint-disable-next-line class-methods-use-this
    private getPlayUriFromChannel(): string {
        const tuningUri = this.mPlayable?.channel?.tuningUri;

        if (tuningUri && tuningUri.length > 0) {
            for (const uri of tuningUri) {
                const splitURI = uri.split('/');
                const drmServerType = this.mDrmServerType;
                const transportEncodingType = this.mTransportEncodingType?.toString();

                if (splitURI.includes(drmServerType) && splitURI.includes(transportEncodingType)) {
                    const match = uri.match(/\bhttps?:\/\/\S+/gi);
                    if (match) return match[0];
                }
            }
        }

        return '';
    }

    private isTransportEncodingType(transportEncodingType?: TransportEncodingType[]): boolean {
        if (
            transportEncodingType &&
            transportEncodingType.length > 0 &&
            transportEncodingType[0] !== TransportEncodingType.Unknown
        )
            return true;
        return false;
    }

    private setDrmServerType(drmServerType: string) {
        this.mDrmServerType = drmServerType;
    }

    private setTransportEncodingType(transportEncodingType: string) {
        this.mTransportEncodingType = transportEncodingType;
    }

    private setUpDrm(response: VideoProviderConfiguration[]): boolean {
        let isInvalidResponse = true;

        response.forEach((videoProviderConfig) => {
            const { providerPartnerId, drmConfig } = videoProviderConfig;
            if (this.mPartnerProviderId === providerPartnerId && drmConfig && isInvalidResponse) {
                const {
                    drmServerType,
                    drmServerUrl,
                    drmCustomHeader,
                    serverSuppliedDeviceIdentifier,
                    transportEncodingType,
                } = drmConfig;
                this.setTransportEncodingType(
                    transportEncodingType ? transportEncodingType[0].toString() : '',
                );
                this.setDrmServerType(drmServerType);
                if (drmServerType === SessionCreateDrmTypeEnum.Widevine) {
                    this.mShakaPlayer.resetPlayerConfiguration();
                    if (
                        drmServerUrl &&
                        drmCustomHeader &&
                        serverSuppliedDeviceIdentifier &&
                        this.isTransportEncodingType(transportEncodingType)
                    ) {
                        const drmInfo: IVpcDrmInfo = {
                            drmType: drmServerType,
                            drmServerUrl,
                            drmCustomHeader,
                            token: serverSuppliedDeviceIdentifier,
                        };
                        this.mShakaPlayer.setUpDRM(drmInfo);
                        isInvalidResponse = false;
                    }
                } else if (
                    drmServerType === SessionCreateDrmTypeEnum.Clear &&
                    this.isTransportEncodingType(transportEncodingType)
                ) {
                    this.mShakaPlayer.resetPlayerConfiguration();
                    isInvalidResponse = false;
                }
                QoEAppLogger.updateContentPayloadDrmAndContainerInfo(
                    drmServerType,
                    transportEncodingType![0],
                );
            }
        });
        return isInvalidResponse;
    }

    private updateLastViewedPosition(screenType: string = APPLICATION_FEATURE_AREA.PLAYER): void {
        PlaybackDataModelImpl.getInstance().updateLastViewedPosition(
            this.getPlayerCurrentTime() * Constants.MILLISECOND_IN_ONE_SECOND,
            this.getSeekRange().start * Constants.MILLISECOND_IN_ONE_SECOND,
            screenType,
        );
    }

    public setupDRM(drmInfo: IVpcDrmInfo): void {
        this.setTransportEncodingType(drmInfo.transportEncodingType || '');
        this.setDrmServerType(drmInfo.drmType);
        this.mShakaPlayer.resetPlayerConfiguration();
        this.mShakaPlayer.setUpDRM(drmInfo);
        if (drmInfo?.transportEncodingType) {
            QoEAppLogger.updateContentPayloadDrmAndContainerInfo(
                drmInfo?.drmType,
                drmInfo?.transportEncodingType as TransportEncodingType,
            );
        }
    }

    public updateDRMInfo(drmInfo: IVpcDrmInfo): void {
        this.mShakaPlayer.updateDRMToken(drmInfo);
    }

    public getDrmInfo(): IVpcDrmInfo {
        return this.mShakaPlayer.getDrmInfo();
    }

    public getHttpsConvertedUrl(url: string): string {
        return url ? url.replace(/^http:\/\//, 'https://') : '';
    }

    //  TODO: Need to check with complete implementation whether we need
    // mVideoEl and mUIContainerRef or not
    public init(videoEl: HTMLVideoElement, uiContainerRef: HTMLDivElement): void {
        this.mVideoEl = videoEl;
        this.mUiContainerRef = uiContainerRef;
        this.mShakaPlayer.initPlayer(videoEl, uiContainerRef);
    }

    public initPlayback() {
        const uri = this.getPlayUriFromChannel();
        this.log('info', 'initPlayback ');

        if (uri.trim().length > 0) {
            this.mStreamingUrl = this.getHttpsConvertedUrl(uri);
            this.mShakaPlayer.setPlayUri(this.mStreamingUrl);
        } else {
            this.mStreamingUrl = '';
            this.log(
                'debug',
                `No URI found from channel tuningUri for DRM Type : ${this.mDrmServerType}`,
            );
        }
    }

    public mount(): void {
        this.log('info', 'mount ');
        this.mUiContainerRef?.classList.remove(VideoManager.CssClasses.HIDDEN);
    }

    public unmount(): void {
        this.log('info', 'unmount ');
        this.clean();
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.HIDDEN);
        this.mPlayerViewModeSubscription.notify(PlayerViewMode.Hidden);
    }

    public homeScreenVideoWindow(): void {
        this.log('info', 'homeScreenVideoWindow ');
        this.mUiContainerRef?.classList.remove(
            VideoManager.CssClasses.HIDDEN,
            VideoManager.CssClasses.MINIMIZE,
            VideoManager.CssClasses.MAXIMIZE,
        );
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.HOME_SCREEN_VIDEO_WINDOW);
        this.mPlayerViewModeSubscription.notify(PlayerViewMode.HomeScreenVideoWindow);
    }

    /** -------Audio Control------- */
    public getAudioTracks(): IVariantTrack[] {
        return this.mShakaPlayer.getAudioTracks();
    }

    public getBufferedInfo(): TimeRanges {
        return this.mShakaPlayer.getBufferedInfo();
    }

    public getSelectedAudioTrack(): IVariantTrack | undefined {
        return this.mShakaPlayer.getVariantTracks().find(({ active }) => active === true);
    }

    public mute(muteAudio: boolean): void {
        this.mShakaPlayer.mute(muteAudio);
    }

    public setAudioTrackByLanguage(language: string): void {
        this.mShakaPlayer.selectAudioTrackByLanguage(language);
    }

    public setAudioTrack(track: IVariantTrack) {
        this.mShakaPlayer.setAudioTrack(track);
    }

    /** -------Availability Control------- */
    public overrideAvailabilityWindowForLinear(): void {
        this.mShakaPlayer.overrideAvailabilityWindowForLinear();
    }

    public overrideAvailabilityWindowForStartOver(): void {
        this.mShakaPlayer.overrideAvailabilityWindowForStartOver();
    }

    public isClosedCaptionActive(): boolean {
        if (
            CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_AUDIO_AND_CC_SUBTITLE_PERSISTANCE_REMOVE_IN_KST_12164,
            )
        ) {
            const ccStatus = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).getItem(
                Storage.Keys.PLAYER_CC_STATUS,
            );
            return !!ccStatus;
        }
        const ccStatus = Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
            Storage.Keys.PLAYER_CC_STATUS,
        );
        return !!ccStatus;
    }

    public toggleClosedCaption(): void {
        let selectedLanguage: string;
        let ccStatus: boolean;
        if (
            CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_AUDIO_AND_CC_SUBTITLE_PERSISTANCE_REMOVE_IN_KST_12164,
            )
        ) {
            ccStatus = Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).getItem(
                Storage.Keys.PLAYER_CC_STATUS,
            );
            Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
                Storage.Keys.PLAYER_CC_STATUS,
                !ccStatus,
            );
            Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).removeItem(
                Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            );

            const textTracks = this.getTextTracks();

            const userTextTrackLanguagePreference = Storage.getInstance(
                Storage.StorageType.LOCAL_STORAGE,
            ).getItem(Storage.Keys.PLAYER_CC_LANGUAGE);

            const deviceLanguage = DeviceModel.getTVProperties().getLang();
            const fallbackLanguage = this.getTextTracks()[0].language;

            // Determine selected language based on priority and availability
            selectedLanguage =
                (userTextTrackLanguagePreference &&
                    textTracks.some(
                        (track) => track.language === userTextTrackLanguagePreference,
                    ) &&
                    userTextTrackLanguagePreference) ||
                (deviceLanguage &&
                    textTracks.some((track) => track.language === deviceLanguage) &&
                    deviceLanguage) ||
                fallbackLanguage;
        } else {
            ccStatus = Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
                Storage.Keys.PLAYER_CC_STATUS,
            );
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.PLAYER_CC_STATUS,
                !ccStatus,
            );
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).removeItem(
                Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            );

            selectedLanguage = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItemWithDefaultValue(Storage.Keys.PLAYER_CC_LANGUAGE, '');
        }

        if (ccStatus) {
            this.mShakaPlayer.setTextTrackVisibility(!ccStatus);
        } else {
            this.mShakaPlayer.setTextTrackByLanguage(selectedLanguage);
        }
    }

    public getPlaybackErrorListener(): ITivoSubscription<LiveTvError> {
        return this.mShakaPlayer.getPlayerErrorListener();
    }

    /** -------Playback Control------- */
    public pause(): void {
        this.mShakaPlayer.pause(true);
    }

    public resume(): void {
        this.mShakaPlayer.pause(false);
    }

    /**
     * Triggers playback from the controller on URL change from session or source.
     * @param {string} uri - The URI to be played. T
     * his URI will be converted to HTTPS format before being set for playback.
     */
    public playUri(uri: string, startTime?: number): void {
        this.mStreamingUrl = uri;
        this.mShakaPlayer.setPlayUri(uri, startTime);
        this.log('info', 'playUri : ', uri);
    }

    public restartPlaybackWithPosition(position: number): void {
        this.mShakaPlayer.play(position);
    }

    public stop(): Promise<void> {
        if (
            CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_AUDIO_AND_CC_SUBTITLE_PERSISTANCE_REMOVE_IN_KST_12164,
            )
        ) {
            Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).removeItem(
                Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            );
        } else {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).removeItem(
                Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            );
        }
        return this.mShakaPlayer.stop();
        /** Commented resetPlayerConfiguration as it is creating V612 error while fast zap.
         * Need to reconsider if something else breaks */
        // this.mShakaPlayer.resetPlayerConfiguration();
    }

    /** -------Player Metadata------- */

    public isDynamicManifest() {
        return this.mShakaPlayer.isDynamicManifest();
    }

    public getAudioVideoCodec(): string {
        const currentTrack = this.getSelectedAudioTrack();
        if (currentTrack) {
            const { videoCodec, audioCodec } = currentTrack;
            return `video/${videoCodec} , audio/${audioCodec}`;
        }
        return `video/ , audio/`;
    }

    public getDuration(): number {
        return this.mShakaPlayer.getDuration();
    }

    public getPlayerCurrentTime(): number {
        return this.mShakaPlayer.getPlayerCurrentTime();
    }

    public getDrmServerType(): string {
        return this.mDrmServerType;
    }

    public getPlayerStats(): IPlayerStats {
        return this.mShakaPlayer.getPlayerStats();
    }

    public getSeekRange(): ISeekRange {
        return this.mShakaPlayer.getSeekRange();
    }

    public getStreamingUrl(): string {
        return this.mStreamingUrl;
    }

    public getTransportEncodingType(): string {
        return this.mTransportEncodingType;
    }

    public getVideoVariants(): IVariantTrack[] {
        return this.mShakaPlayer.getVariantTracks();
    }

    /** -------Playback State------- */

    public getPlayerStateChangeListener(): ITivoSubscription<IPlayerStatus> {
        return this.mShakaPlayer.getPlayerStateChangeListener();
    }

    public setUpPlayer(playable: IPlayable, partnerId: string) {
        this.mPartnerProviderId = partnerId;
        this.mPlayable = playable;
        const isMobiDrmEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_MOBI_DRM_REMOVE_IN_KST_7121,
        );
        if (!isMobiDrmEnabled) {
            this.fetchVPConfigurations(false);
        }
    }

    public clean(): void {
        this.mShakaPlayer.stop();
    }

    public setPlayPosition(position: number): void {
        this.mShakaPlayer.setPlayPosition(position);
    }

    /** -------Text Track Control------- */

    public getSelectedTextTrack(): ITextTrack {
        const textTracks = this.getTextTracks();
        const selectedTextTrack = textTracks.find(({ active }) => active === true);
        return selectedTextTrack || textTracks[0];
    }

    public getTextTracks(): ITextTrack[] {
        const mimeTypeMP4 = 'application/mp4';
        const isSubtitleSmpteAllowed = DeviceModel.getTiVoSettings().isSubtitleSmpteAllowed();
        const isClosedCaption708Allowed = DeviceModel.getTiVoSettings().isClosedCaption708Allowed();
        const isClosedCaption608Allowed = DeviceModel.getTiVoSettings().isClosedCaption608Allowed();

        const mimeTypePriority = [
            ...(isSubtitleSmpteAllowed ? ['application/ttml+xml', mimeTypeMP4] : []),
            ...(isClosedCaption708Allowed ? ['708'] : []),
            ...(isClosedCaption608Allowed ? ['608'] : []),
        ];
        const filterTrack: ITextTrack[] = [];

        for (const entry of this.mShakaPlayer.getTextTracks()) {
            // Check for each mime in priority order
            for (const mimeType of mimeTypePriority) {
                if (entry.mimeType === mimeType || entry.mimeType.includes(mimeType)) {
                    filterTrack.push(entry);
                    break; // Once we assign a mime, stop checking
                }
            }
        }

        return filterTrack;
    }

    public isTextTrackChangeSupported(): boolean {
        return this.getTextTracks().length > 1;
    }

    public isTextTrackSupported(): boolean {
        return this.mShakaPlayer.isTextTrackSupported();
    }

    public setTextTrack(track: ITextTrack): void {
        this.mShakaPlayer.selectTextTrack(track);
    }

    /** -------View Control------- */

    public getPlayerViewChangeListener(): ITivoSubscription<PlayerViewMode> {
        return this.mPlayerViewModeSubscription;
    }

    public getPlayerViewMode(): string {
        return this.mPlayerViewMode;
    }

    public maximize(): void {
        this.mUiContainerRef?.classList.remove(
            VideoManager.CssClasses.HIDDEN,
            VideoManager.CssClasses.MINIMIZE,
            VideoManager.CssClasses.HOME_SCREEN_VIDEO_WINDOW,
        );
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.MAXIMIZE);
        this.mPlayerViewModeSubscription.notify(PlayerViewMode.FullScreen);
    }

    public minimize(): void {
        this.mUiContainerRef?.classList.remove(
            VideoManager.CssClasses.MAXIMIZE,
            VideoManager.CssClasses.HOME_SCREEN_VIDEO_WINDOW,
        );
        this.mUiContainerRef?.classList.add(VideoManager.CssClasses.MINIMIZE);
        if (this.isTrickplayEnabled()) {
            this.getTrickplayControl().stopScrubbing();
        }
        this.mPlayerViewModeSubscription.notify(PlayerViewMode.VideoWindow);
    }

    /** -------Trickplay Control------- */

    public isTrickplayEnabled(): boolean {
        return this.getTrickplayControl().isScrubbing();
    }

    public getTrickplayControl(): ITrickplayControl {
        return this.mTrickplayControl;
    }
}
